import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { type DateRange } from '@mint-lib/tw-components/patterns';
import { Schedule } from '@mint-sdk/api-ai-assistant';
import { format, parse } from 'date-fns';

import { useCreateScheduleMutation } from '../../queries/schedule.js';

const DEFAULT_SCHEDULING = 'daily';
const DEFAULT_SCHEDULE_TIME = () => parse('10:00', 'HH:mm', new Date());

const modifyDate = (date: Date, time: Date): string => {
  const mergedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds(),
  );
  return format(mergedDate, "yyyy-MM-dd'T'HH:mm:ss");
};

export const useScheduleMessage = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const [scheduling, setScheduling] = useState<string>(DEFAULT_SCHEDULING);
  const [scheduleTime, setScheduleTime] = useState<Date | null>(
    DEFAULT_SCHEDULE_TIME(),
  );
  const [scheduleDateRange, setScheduleDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const { mutateAsync: createSchedule, isPending: isSchedulePending } =
    useCreateScheduleMutation();

  const isScheduleReady = useMemo(() => {
    return (
      !!scheduling &&
      scheduleTime !== null &&
      !!scheduleDateRange?.from &&
      !!scheduleDateRange?.to
    );
  }, [scheduling, scheduleTime, scheduleDateRange]);

  const onSchedule = useCallback(
    async (query: string) => {
      if (
        !scheduling ||
        !scheduleTime ||
        !scheduleDateRange?.from ||
        !scheduleDateRange?.to
      ) {
        return;
      }

      if (!query) return;

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedStartDate = modifyDate(
        scheduleDateRange.from,
        scheduleTime,
      );
      const formattedEndDate = modifyDate(scheduleDateRange.to, scheduleTime);

      try {
        await createSchedule({
          scheduling:
            scheduling as Schedule.CreateScheduleRequestType['scheduling'],
          userTimezone,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          query,
        });

        enqueueSnackbar({
          variant: 'success',
          title: t('Chat successfully scheduled'),
        });

        return true;
      } catch {
        enqueueSnackbar({
          variant: 'error',
          title: t('Error'),
          subTitle: t('Failed to schedule chat'),
          textLayout: 'inline',
        });

        return false;
      }
    },
    [t, scheduling, scheduleTime, scheduleDateRange, createSchedule],
  );

  return {
    scheduling,
    setScheduling,
    scheduleTime,
    setScheduleTime,
    scheduleDateRange,
    setScheduleDateRange,
    isScheduleReady,
    isSchedulePending,
    onSchedule,
  };
};
